import { useQuery } from "@tanstack/react-query";

import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  listPosts,
  listComments,
  postsByContextIdAndCreatedAt,
  postsByPostAuthorIdAndCreatedAt,
  postsByAudienceIDAndCreatedAt,
} from "@/graphql/queries";

import {
  createPost,
  createComment,
  updatePost,
  deletePost,
  deleteComment,
  updateComment,
} from "@/graphql/mutations";

import {
  IPayloadCreatePost,
  ICreatePostMutation,
  IUpdatePost,
  IUpdatePostMutation,
  IDataDeletePost,
  IDeletePostMutation,
  IDeleteCommentMutation,
  IPayloadCreateComment,
  ICreateCommentMutation,
  IUpdateComment,
  IUpdateCommentMutation,
} from "@/types";
import { PostsByAudienceIDAndCreatedAtQuery } from "@/graphql/graphql";

const fetchPosts = async (
  contextId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
) => {
  const filter: {
    contextId?: { eq: string };
    postAuthorId?: { eq: string };
    contextType: { eq: string };
    category: { eq: string };
    _deleted: { ne: boolean };
  } = {
    contextType: { eq: contextType },
    category: { eq: category ? category : "POST" },
    _deleted: { ne: true },
  };

  if (contextType === "USER") {
    filter.postAuthorId = { eq: contextId };
  } else {
    filter.contextId = { eq: contextId };
  }

  const response: any = await API.graphql(
    graphqlOperation(listPosts, {
      filter,
    }),
  );
  const records: any[] = response?.data?.listPosts?.items ?? [];

  return records;
};

const fetchPostsByContextId = async (
  contextId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
  sortBy?: "ASC" | "DESC",
) => {
  const filter: {
    contextType: { eq: string };
    category: { eq: string };
    _deleted: { ne: boolean };
  } = {
    contextType: { eq: contextType },
    category: { eq: category ? category : "POST" },
    _deleted: { ne: true },
  };

  const response: any = await API.graphql(
    graphqlOperation(postsByContextIdAndCreatedAt, {
      contextId,
      sortDirection: sortBy || "DESC",
      filter,
    }),
  );
  const records: any[] =
    response?.data?.postsByContextIdAndCreatedAt?.items ?? [];

  return records;
};

const fetchPostsByAuthorId = async (
  postAuthorId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
  sortBy?: "ASC" | "DESC",
) => {
  const filter: {
    contextType: { eq: string };
    category: { eq: string };
    _deleted: { ne: boolean };
  } = {
    contextType: { eq: contextType },
    category: { eq: category ? category : "POST" },
    _deleted: { ne: true },
  };

  const response: any = await API.graphql(
    graphqlOperation(postsByPostAuthorIdAndCreatedAt, {
      postAuthorId,
      sortDirection: sortBy || "DESC",
      filter,
    }),
  );
  const records: any[] =
    response?.data?.postsByPostAuthorIdAndCreatedAt?.items ?? [];

  return records;
};

const fetchCommentByPost = async (postId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listComments, {
      filter: {
        commentPostId: { eq: postId },
        _deleted: { ne: true },
      },
    }),
  );
  const records: any[] = response?.data?.listComments?.items ?? [];

  return records;
};

const useCreatePost = async (payload: IPayloadCreatePost) => {
  const records = await API.graphql<GraphQLQuery<ICreatePostMutation>>({
    query: createPost,
    variables: {
      input: payload,
    },
  });

  return records?.data?.createPost;
};

const useUpdatePost = async (payload: IUpdatePost) => {
  const records = await API.graphql<GraphQLQuery<IUpdatePostMutation>>({
    query: updatePost,
    variables: {
      input: payload,
    },
  });

  return records?.data?.updatePost;
};

const useDeletePost = async (payload: IDataDeletePost) => {
  const records = await API.graphql<GraphQLQuery<IDeletePostMutation>>({
    query: deletePost,
    variables: {
      input: payload,
    },
  });

  return records?.data?.deletePost;
};

const useCreateComment = async (payload: IPayloadCreateComment) => {
  const records = await API.graphql<GraphQLQuery<ICreateCommentMutation>>({
    query: createComment,
    variables: {
      input: payload,
    },
  });

  return records?.data?.createComment;
};

const useDeleteComment = async (payload: IDataDeletePost) => {
  const records = await API.graphql<GraphQLQuery<IDeleteCommentMutation>>({
    query: deleteComment,
    variables: {
      input: payload,
    },
  });

  return records?.data?.deleteComment;
};

const useUpdateComment = async (payload: IUpdateComment) => {
  const records = await API.graphql<GraphQLQuery<IUpdateCommentMutation>>({
    query: updateComment,
    variables: {
      input: payload,
    },
  });

  return records?.data?.updateComment;
};

const useGetPosts = (
  contextId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
) => {
  return useQuery({
    queryKey: ["KEY_GET_POSTS", contextId, contextType, category],
    queryFn: () => fetchPosts(contextId, contextType, category),
  });
};

const useGetPostsByAuthorId = (
  postAuthorId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
) => {
  return useQuery({
    queryKey: [
      "KEY_GET_POSTS_BY_AUTHOR_ID",
      postAuthorId,
      contextType,
      category,
    ],
    queryFn: () => fetchPostsByAuthorId(postAuthorId, contextType, category),
  });
};

const useGetPostsByContextId = (
  contextId: string,
  contextType: "EVENT" | "CIRCLE" | "USER",
  category?: "POST" | "ACTIVITY",
) => {
  return useQuery({
    queryKey: ["KEY_GET_POSTS_BY_CONTEXT_ID", contextId, contextType, category],
    queryFn: () => fetchPostsByContextId(contextId, contextType, category),
  });
};

const useGetCommentByPost = (postId: string, enabled: boolean) => {
  return useQuery({
    queryKey: ["KEY_GET_COMMENT_BY_POST", postId],
    queryFn: () => fetchCommentByPost(postId),
    enabled,
  });
};

const useGetPostByAudience = (audienceID: string) => {
  return useQuery({
    queryKey: ["KEY_GET_POSTS_BY_CONTEXT_ID_AUDIENCE", audienceID],
    queryFn: async () => {
      const records = await API.graphql<
        GraphQLQuery<PostsByAudienceIDAndCreatedAtQuery>
      >({
        query: postsByAudienceIDAndCreatedAt,
        variables: {
          audienceID,
        },
      });

      return records.data?.postsByAudienceIDAndCreatedAt?.items || [];
    },
  });
};

export {
  useCreatePost,
  useGetPosts,
  useGetPostsByContextId,
  useGetPostsByAuthorId,
  useUpdatePost,
  useDeletePost,
  useGetCommentByPost,
  useCreateComment,
  useDeleteComment,
  useUpdateComment,
  useGetPostByAudience
};
